import { useEffect, useState } from 'react';

// Matches `ChipsContainer`'s collapsed `max-height` in styles.ts. Collection names are free text
// (unlike resource types, a fixed vocabulary of short labels), so how many chips fit in that height
// before wrapping past it varies with how long those names are — a raw item count (as the types
// filter uses) can both under- and over-trigger the "View more" button, and did: RES-1247 hid
// collections with no way to reveal them because the item count never crossed the threshold even
// though the collapsed container was already clipping content. Measuring the actual overflow
// instead makes the button track what's really on screen, whatever the chip lengths are.
export const COLLAPSED_CHIPS_CONTAINER_HEIGHT_PX = 82;

/**
 * Whether a collapsible chips container is currently clipping content, based on its measured
 * `scrollHeight` rather than an item count. Re-checked whenever `collectionsCount` changes;
 * deliberately not re-checked when the container is expanded/collapsed, since overflow is a
 * property of the content, not of the current expand state — recomputing on every toggle would
 * fight the container's own `max-height` transition.
 */
export const useIsChipsContainerOverflowing = (
  ref: React.RefObject<HTMLElement>,
  collectionsCount: number,
): boolean => {
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    setIsOverflowing(Boolean(ref.current) && ref.current.scrollHeight > COLLAPSED_CHIPS_CONTAINER_HEIGHT_PX);
  }, [ref, collectionsCount]);

  return isOverflowing;
};
